import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import type { DocumentContent, DocumentType } from '@/types';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 11,
    paddingTop: 72,
    paddingBottom: 72,
    paddingLeft: 80,
    paddingRight: 80,
    backgroundColor: '#FFFDF7',
    color: '#1a1a1a',
  },
  header: {
    marginBottom: 32,
    borderBottomWidth: 2,
    borderBottomColor: '#d97706',
    borderBottomStyle: 'solid',
    paddingBottom: 16,
  },
  title: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    color: '#1a1a1a',
    marginBottom: 6,
  },
  subject: {
    fontSize: 11,
    color: '#6b7280',
    fontStyle: 'italic',
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
    fontSize: 10,
    color: '#374151',
  },
  metaBlock: {
    flex: 1,
  },
  metaLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: '#9ca3af',
    textTransform: 'uppercase',
    marginBottom: 3,
    letterSpacing: 1,
  },
  metaValue: {
    fontSize: 10,
    color: '#1f2937',
    lineHeight: 1.4,
  },
  section: {
    marginBottom: 20,
  },
  sectionLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
    color: '#92400e',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 6,
  },
  sectionBody: {
    fontSize: 11,
    lineHeight: 1.6,
    color: '#1f2937',
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    borderBottomStyle: 'solid',
    marginVertical: 12,
  },
  disclaimerSection: {
    marginTop: 32,
    padding: 12,
    backgroundColor: '#fef3c7',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#fcd34d',
    borderStyle: 'solid',
  },
  disclaimerLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: '#92400e',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 4,
  },
  disclaimerBody: {
    fontSize: 9,
    color: '#78350f',
    lineHeight: 1.5,
  },
  footer: {
    position: 'absolute',
    bottom: 36,
    left: 80,
    right: 80,
    textAlign: 'center',
    fontSize: 9,
    color: '#9ca3af',
  },
});

interface Props {
  title: string;
  documentType: DocumentType;
  content: DocumentContent;
}

export function LegalDocumentPDF({ title, content }: Props) {
  const nonDisclaimerSections = content.sections.filter(s => s.id !== 'disclaimer');
  const disclaimerSection = content.sections.find(s => s.id === 'disclaimer');

  return (
    <Document title={title} author="notlegal.chat">
      <Page size="LETTER" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>{title}</Text>
          {content.metadata.subject && (
            <Text style={styles.subject}>Re: {content.metadata.subject}</Text>
          )}
        </View>

        {/* Parties & Date Meta */}
        <View style={styles.metaRow}>
          <View style={styles.metaBlock}>
            <Text style={styles.metaLabel}>From</Text>
            <Text style={styles.metaValue}>{content.parties.sender_name}</Text>
            {content.parties.sender_address && (
              <Text style={styles.metaValue}>{content.parties.sender_address}</Text>
            )}
          </View>
          <View style={styles.metaBlock}>
            <Text style={styles.metaLabel}>To</Text>
            <Text style={styles.metaValue}>{content.parties.recipient_name}</Text>
            {content.parties.recipient_address && (
              <Text style={styles.metaValue}>{content.parties.recipient_address}</Text>
            )}
          </View>
          <View style={styles.metaBlock}>
            <Text style={styles.metaLabel}>Date</Text>
            <Text style={styles.metaValue}>{content.metadata.date}</Text>
            {content.metadata.jurisdiction && (
              <>
                <Text style={[styles.metaLabel, { marginTop: 8 }]}>Jurisdiction</Text>
                <Text style={styles.metaValue}>{content.metadata.jurisdiction}</Text>
              </>
            )}
          </View>
        </View>

        <View style={styles.divider} />

        {/* Document Sections */}
        {nonDisclaimerSections.map((section, index) => (
          <View key={section.id} style={styles.section}>
            <Text style={styles.sectionLabel}>{section.label}</Text>
            <Text style={styles.sectionBody}>{section.body}</Text>
            {index < nonDisclaimerSections.length - 1 && <View style={styles.divider} />}
          </View>
        ))}

        {/* Disclaimer */}
        {disclaimerSection && (
          <View style={styles.disclaimerSection}>
            <Text style={styles.disclaimerLabel}>{disclaimerSection.label}</Text>
            <Text style={styles.disclaimerBody}>{disclaimerSection.body}</Text>
          </View>
        )}

        {/* Footer */}
        <Text style={styles.footer} fixed>
          Generated by notlegal.chat — Not Legal Advice
        </Text>
      </Page>
    </Document>
  );
}
